import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Center,
  Spinner,
  Flex,
  FormControl,
  FormLabel,
  Select,
  useToast,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getAllBookings, getAllUsers, getAllPayments, Booking, Payment, getAllServiceTypes, ServiceType } from '../services/firestore';

interface ChartData {
  name: string;
  value: number;
}

interface TimeSeriesData {
  date: string;
  bookings: number;
  revenue: number;
}

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [bookingData, setBookingData] = useState<Booking[]>([]);
  const [paymentData, setPaymentData] = useState<Payment[]>([]);
  const [userData, setUserData] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const toast = useToast();

  // Helper to format cents to dollars
  const formatAmount = (amount: number | undefined) => `$${((amount ?? 0) / 100).toFixed(2)}`;

  // Helper to get service type name
  const getServiceTypeName = (serviceTypeId: string) => {
    const serviceType = serviceTypes.find(st => st.id === serviceTypeId);
    return serviceType ? serviceType.name : serviceTypeId;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const [bookings, payments, users, serviceTypesData] = await Promise.all([
        getAllBookings(),
        getAllPayments(),
        getAllUsers(),
        getAllServiceTypes(),
      ]);

      setBookingData(bookings);
      setPaymentData(payments);
      setUserData(users);
      setServiceTypes(serviceTypesData);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast({
        title: 'Error loading analytics',
        description: 'Failed to fetch analytics data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate time series data for the last 7 days
  const generateTimeSeriesData = (): TimeSeriesData[] => {
    const data: TimeSeriesData[] = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const dayBookings = bookingData.filter(booking => {
        const bookingDate = (booking.scheduledTime || booking.scheduledDate)?.toDate?.() || new Date((booking.scheduledTime || booking.scheduledDate) as any);
        return bookingDate.toDateString() === date.toDateString();
      });
      
      // Calculate revenue from booking amounts (excluding cancelled bookings)
      const dayRevenue = dayBookings
        .filter(booking => booking.status !== 'cancelled')
        .reduce((sum, booking) => sum + (booking.amount || 0), 0);
      
      data.push({
        date: dateStr,
        bookings: dayBookings.length,
        revenue: dayRevenue,
      });
    }
    
    return data;
  };

  // Generate service type distribution
  const generateServiceDistribution = (): ChartData[] => {
    const serviceCounts: { [key: string]: number } = {};
    
    bookingData.forEach(booking => {
      const serviceName = getServiceTypeName(booking.serviceType);
      serviceCounts[serviceName] = (serviceCounts[serviceName] || 0) + 1;
    });
    
    return Object.entries(serviceCounts).map(([service, count]) => ({
      name: service,
      value: count,
    }));
  };

  // Generate status distribution
  const generateStatusDistribution = (): ChartData[] => {
    const statusCounts: { [key: string]: number } = {};
    
    bookingData.forEach(booking => {
      statusCounts[booking.status] = (statusCounts[booking.status] || 0) + 1;
    });
    
    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status.replace('_', ' '),
      value: count,
    }));
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const timeSeriesData = generateTimeSeriesData();
  const serviceDistribution = generateServiceDistribution();
  const statusDistribution = generateStatusDistribution();

  // Calculate total revenue from booking amounts (excluding cancelled bookings)
  const totalRevenue = bookingData
    .filter(booking => booking.status !== 'cancelled')
    .reduce((sum, booking) => sum + (booking.amount || 0), 0);

  const totalBookings = bookingData.length;
  const completedBookings = bookingData.filter(booking => booking.status === 'completed').length;
  const completionRate = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          Analytics & Reports
        </Heading>
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">Time Range</FormLabel>
          <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </Select>
        </FormControl>
      </Flex>

      {/* Key Metrics */}
      <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={6} mb={8}>
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Revenue</StatLabel>
          <StatNumber>{formatAmount(totalRevenue)}</StatNumber>
          <StatHelpText>All time (excluding cancelled)</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Bookings</StatLabel>
          <StatNumber>{totalBookings}</StatNumber>
          <StatHelpText>All time</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Completion Rate</StatLabel>
          <StatNumber>{completionRate}%</StatNumber>
          <StatHelpText>Service completion</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Active Users</StatLabel>
          <StatNumber>{userData.filter(user => user.isActive).length}</StatNumber>
          <StatHelpText>Currently active</StatHelpText>
        </Stat>
      </Grid>

      {/* Charts Grid */}
      <Grid templateColumns="repeat(auto-fit, minmax(500px, 1fr))" gap={6}>
        {/* Revenue & Bookings Trend */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Revenue & Bookings Trend</Heading>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                formatter={(value: any, name: string) => [
                  name === 'revenue' ? formatAmount(value) : value,
                  name === 'revenue' ? 'Revenue' : 'Bookings'
                ]}
              />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                stroke="#8884d8"
                name="Revenue"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="bookings"
                stroke="#82ca9d"
                name="Bookings"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Service Distribution */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Service Distribution</Heading>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={serviceDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {serviceDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Booking Status Distribution */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Booking Status Distribution</Heading>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Monthly Revenue */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Daily Revenue</Heading>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip 
                formatter={(value: any) => [formatAmount(value), 'Revenue']}
              />
              <Bar dataKey="revenue" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
    </Box>
  );
};

export default Analytics; 